import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlatformAdmin } from '../../../api/platformAdmins.api';
import type { PlatformRole } from '../../../api/platformRoles.api';

const inputCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all bg-white';

interface UserFormModalProps {
  user: PlatformAdmin | null;
  roles: PlatformRole[];
  isOpen: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    email: string;
    platform_role_id: number;
    status: string;
    password?: string;
  }) => void | Promise<void>;
}

export default function UserFormModal({
  user,
  roles,
  isOpen,
  saving,
  onClose,
  onSave,
}: UserFormModalProps) {
  const { t } = useTranslation();
  const isEdit = !!user;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<number | ''>('');
  const [password, setPassword] = useState('');
  const [active, setActive] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    setName(user?.name ?? '');
    setEmail(user?.email ?? '');
    setRoleId(user?.platform_role_id ?? roles[0]?.id ?? '');
    setPassword('');
    setActive(user ? user.status === 'active' : true);
    setErrors({});
  }, [isOpen, user, roles]);

  if (!isOpen) return null;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Required';
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) e.email = 'Valid email required';
    if (!roleId) e.role = 'Required';
    if (!isEdit && !password.trim()) e.password = 'Required for new admin';
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    void onSave({
      name: name.trim(),
      email: email.trim(),
      platform_role_id: Number(roleId),
      status: active ? 'active' : 'suspended',
      password: password || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">
            {isEdit ? t('users.form.title_edit') : t('users.form.title_add')}
          </h3>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">{t('users.form.name')}</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} ${errors.name ? 'border-rose-400' : ''}`} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">{t('users.form.email')}</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} ${errors.email ? 'border-rose-400' : ''}`} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">{t('users.form.role')}</label>
            <select
              value={roleId}
              onChange={(e) => setRoleId(e.target.value ? Number(e.target.value) : '')}
              className={`${inputCls} ${errors.role ? 'border-rose-400' : ''}`}
            >
              <option value="">—</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {isEdit ? t('users.form.password_edit') : t('users.form.password')}
            </label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputCls} pe-10 ${errors.password ? 'border-rose-400' : ''}`}
              />
              <button type="button" className="absolute end-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPass((v) => !v)}>
                <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>
            {isEdit ? <p className="text-xs text-gray-400 mt-1">{t('users.form.password_hint')}</p> : null}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            {t('users.form.active')}
          </label>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium">
            {t('users.form.cancel')}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSubmit}
            className="flex-1 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60"
          >
            {saving ? '…' : t('users.form.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
